import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { fetchProducts, fetchCategories } from '../../services/api';

const ProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategoryParam = searchParams.get('category') || 'all';

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [prodData, catData] = await Promise.all([
          fetchProducts(),
          fetchCategories().catch(() => ({ categories: [] }))
        ]);
        setProducts(prodData.products || []);
        setCategories(catData.categories || []);
        setLoading(false);
      } catch (err) {
        setError('Failed to load products. Please try again later.');
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleCategorySelect = (categoryName) => {
    if (categoryName === 'all') {
      searchParams.delete('category');
      setSearchParams(searchParams);
    } else {
      setSearchParams({ category: categoryName });
    }
  };

  const filteredProducts = products.filter(product => {
    // Filter by search term
    const name = product?.name || '';
    const desc = product?.description || '';
    const term = searchTerm.toLowerCase();
    const matchesSearch = name.toLowerCase().includes(term) || desc.toLowerCase().includes(term);

    if (!matchesSearch) return false;

    // Filter by active category
    if (activeCategoryParam === 'all') return true;

    const prodCats = product?.categories || [];
    return prodCats.some(
      c => c.name.toLowerCase() === activeCategoryParam.toLowerCase() ||
           c.slug.toLowerCase() === activeCategoryParam.toLowerCase()
    );
  });

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-[#1E4C9A] to-[#1A8D32] text-white py-12 sm:py-16 mb-8 sm:mb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-3 sm:mb-4">Our Agricultural Products & Equipment</h1>
          <p className="text-lg sm:text-xl max-w-2xl mx-auto opacity-90 px-4">Discover our comprehensive range of high-quality agricultural solutions designed to revolutionize modern farming.</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Toolbar & Search */}
        <div className="flex flex-col md:flex-row justify-between items-center mb-6 sm:mb-8 gap-4">
          <div className="w-full md:w-96 relative">
            <input 
              type="text" 
              placeholder="Search products..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1A8D32] focus:border-transparent outline-none transition-all"
            />
            <svg className="w-5 h-5 text-gray-400 absolute left-3 top-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>

        {/* Category Filter Pills */}
        {!loading && !error && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-500">Filter by Category:</h2>
              {activeCategoryParam !== 'all' && (
                <button
                  onClick={() => handleCategorySelect('all')}
                  className="text-xs text-[#1E4C9A] hover:underline font-medium"
                >
                  Clear Category Filter
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => handleCategorySelect('all')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                  activeCategoryParam === 'all'
                    ? 'bg-[#1A8D32] text-white shadow-md'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                All Products ({products.length})
              </button>

              {categories.map((cat) => {
                const count = products.filter(p => 
                  (p.categories || []).some(c => c.category_id === cat.category_id || c.name === cat.name)
                ).length;

                const isActive = activeCategoryParam.toLowerCase() === cat.name.toLowerCase() || 
                                 activeCategoryParam.toLowerCase() === cat.slug.toLowerCase();

                return (
                  <button
                    key={cat.category_id}
                    onClick={() => handleCategorySelect(cat.name)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 flex items-center gap-1.5 ${
                      isActive
                        ? 'bg-[#1A8D32] text-white shadow-md'
                        : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                    }`}
                  >
                    <span>{cat.name}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                      isActive ? 'bg-green-800 text-white' : 'bg-gray-100 text-gray-500'
                    }`}>
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Content */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
              <div key={n} className="bg-white rounded-xl shadow-sm overflow-hidden animate-pulse">
                <div className="w-full h-48 bg-gray-200"></div>
                <div className="p-4 sm:p-5">
                  <div className="h-6 bg-gray-200 rounded w-3/4 mb-4"></div>
                  <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-5/6 mb-4"></div>
                  <div className="flex justify-between items-center mb-4">
                    <div className="h-6 bg-gray-200 rounded w-1/3"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                  </div>
                  <div className="h-10 bg-gray-200 rounded w-full"></div>
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="text-center py-12 bg-white rounded-xl shadow-sm border border-red-100 mx-4">
            <svg className="w-12 h-12 sm:w-16 sm:h-16 text-red-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">Error Loading Products</h3>
            <p className="text-sm sm:text-base text-gray-600 mb-4">{error}</p>
            <button 
              onClick={() => window.location.reload()} 
              className="bg-[#1E4C9A] text-white px-6 py-2 rounded-lg hover:bg-blue-900 transition-colors"
            >
              Try Again
            </button>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-12 sm:py-16 bg-white rounded-xl shadow-sm mx-4">
            <svg className="w-12 h-12 sm:w-16 sm:h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">No products found</h3>
            <p className="text-sm sm:text-base text-gray-600 mb-6">We couldn't find any products matching your criteria.</p>
            <button 
              onClick={() => {
                setSearchTerm('');
                handleCategorySelect('all');
              }} 
              className="bg-[#1A8D32] text-white px-6 py-2 rounded-lg hover:bg-green-700 transition-colors"
            >
              Reset All Filters
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
            {filteredProducts.map(product => (
              <div key={product.product_id} className="bg-white rounded-xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden flex flex-col h-full border border-gray-100 group">
                <div className="relative aspect-w-4 aspect-h-3 overflow-hidden bg-gray-100 pt-[75%]">
                  <img 
                    src={product.image_url || '/assets/images/placeholder-product.jpg'} 
                    alt={product.alt_text || product.name}
                    className="absolute top-0 left-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = '/assets/images/error.jpeg';
                    }}
                  />
                  <div className="absolute top-3 right-3">
                    {product.available ? (
                      <span className="bg-green-100 text-green-800 text-xs font-semibold px-2.5 py-1 rounded-full border border-green-200 backdrop-blur-sm bg-opacity-90">In Stock</span>
                    ) : (
                      <span className="bg-red-100 text-red-800 text-xs font-semibold px-2.5 py-1 rounded-full border border-red-200 backdrop-blur-sm bg-opacity-90">Out of Stock</span>
                    )}
                  </div>
                </div>
                
                <div className="p-4 sm:p-5 flex flex-col flex-grow">
                  {/* Category badges */}
                  {product.categories && product.categories.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-2">
                      {product.categories.map(cat => (
                        <button
                          key={cat.category_id}
                          onClick={(e) => {
                            e.preventDefault();
                            handleCategorySelect(cat.name);
                          }}
                          className="bg-blue-50 text-[#1E4C9A] hover:bg-[#1E4C9A] hover:text-white text-[11px] font-semibold px-2 py-0.5 rounded transition-colors"
                        >
                          {cat.name}
                        </button>
                      ))}
                    </div>
                  )}

                  <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-2 line-clamp-1">{product.name}</h3>
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2 flex-grow">{product.description}</p>
                  
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-lg sm:text-xl font-bold text-[#1E4C9A]">Rs. {product.price?.toLocaleString()}</span>
                    <div className="flex items-center">
                      <span className="text-yellow-400 flex items-center">
                        <svg className="w-4 h-4 fill-current" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                        <span className="ml-1 text-sm font-medium text-gray-700">{Math.round((product.rating || 0) * 10) / 10}</span>
                      </span>
                      <span className="text-xs text-gray-500 ml-1">({product.review_count || 0})</span>
                    </div>
                  </div>
                  
                  <Link 
                    to={`/products/${product.product_id}`} 
                    className="w-full text-center bg-gray-50 text-[#1E4C9A] hover:bg-[#1E4C9A] hover:text-white border border-[#1E4C9A] font-medium py-2 px-4 rounded-lg transition-colors duration-300"
                  >
                    View Details
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductsPage;